import React, {
  useCallback, useEffect, useRef, useState, memo,
} from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  compose, filter, get, includes, map, reject, toNumber, toString, startsWith, find, isNil,
} from 'lodash/fp';
import {
  CheckBox, CheckBoxOutlineBlank, RadioButtonChecked, RadioButtonUnchecked,
} from '@material-ui/icons';
import { isEllipsisActive, hexToRgb } from '../../../common/utils';

const S = {
  QuestionBlock: styled.div.attrs(({ position, size }) => ({
    style: {
      top: `${position.y}%`,
      left: `${position.x}%`,
      width: `${size.width}%`,
      height: `${size.height}%`,
    },
  }))`
    position: absolute;
    border: 2px solid ${({ theme }) => theme.palette.status.note.main};
    border-radius: 2px;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.24);
  `,
  QuestionNumber: styled.div`
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(calc(-100% - 2px), calc(-100% - 2px));
    padding: ${({ large }) => (large ? '3px 6px' : '2px 4px')};
    border-radius: ${({ large }) => (large ? '12px 12px 0 12px' : '8px 8px 0 8px')};
    color: ${({ theme }) => theme.palette.primary.contrastText};
    background-color: ${({ theme }) => theme.palette.status.note.main};
    font-size: ${({ large, theme }) => theme.typography.pxToRem(large ? 13 : 10)};
    font-weight: 700;
    line-height: ${({ large }) => (large ? 1.54 : 1.4)};
    box-shadow: ${({ large }) => (large ? '0 1px 4px 0 rgba(0, 0, 0, 0.32);' : '0 1px 2px 0 rgba(0, 0, 0, 0.32)')};
  `,
  OptionItem: styled.li`
    position: absolute;
    top: ${({ position }) => `${position.y}%`};
    left: ${({ position }) => `${position.x}%`};
    display: flex;
    align-items: start;
  `,
  OptionNumber: styled.div`
    min-height: ${({ large }) => (large ? 20 : 16)}px;
    padding: 0px 4px;
    border-radius: 2px;
    color: ${({ theme }) => theme.palette.primary.contrastText};
    background-color: ${({ theme }) => theme.palette.primary.light};
    font-size: ${({ large, theme }) => theme.typography.pxToRem(large ? 13 : 10)};
    font-weight: 500;
    line-height: ${({ large }) => (large ? 1.54 : 1.4)};
    text-align: center;
    white-space: nowrap;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.32);
  `,
  CheckBox: styled(({ large, ...otherProps }) => <CheckBox {...otherProps} />)`
    position: relative;
    color: ${({ theme }) => theme.palette.primary.light};
    font-size: ${({ large }) => (large ? '1.25rem' : '1rem')};
  `,
  CheckBoxOutlineBlank: styled(({ large, ...otherProps }) => <CheckBoxOutlineBlank {...otherProps} />)`
    position: relative;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ large }) => (large ? '1.25rem' : '1rem')};
  `,
  RadioButtonChecked: styled(({ large, ...otherProps }) => <RadioButtonChecked {...otherProps} />)`
    position: relative;
    color: ${({ theme }) => theme.palette.primary.light};
    font-size: ${({ large }) => (large ? '1.25rem' : '1rem')};
  `,
  RadioButtonUnchecked: styled(({ large, ...otherProps }) => <RadioButtonUnchecked {...otherProps} />)`
    position: relative;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ large }) => (large ? '1.25rem' : '1rem')};
  `,
  FieldRect: styled.div.attrs(({ field }) => ({
    style: {
      top: `${field.y}%`,
      left: `${field.x}%`,
      width: `${field.width}%`,
    },
  }))`
    position: absolute;
    min-height: ${({ large }) => (large ? 20 : 16)}px;
    z-index: ${({ ellipsis }) => (ellipsis ? 0 : 100)};
    padding: 0px 2px 0px 8px;
    display: flex;
    align-items: center;
    background-color: ${({ theme }) => theme.palette.blue[10]};
    box-shadow: 0 1px 2px 0 rgba(93, 108, 164, 0.54);
  `,
  OtherResponseRect: styled.div.attrs(({ field, viewportWidth }) => ({
    style: {
      top: `${field.position.y}%`,
      left: `${field.position.x}%`,
      width: `${(field.size.width / 100) * viewportWidth}px`,
    },
  }))`
    min-height: ${({ large }) => (large ? 20 : 16)}px;
    z-index: ${({ ellipsis }) => (ellipsis ? 0 : 100)};
    padding: 0px 2px 0px 8px;
    display: flex;
    align-items: center;
    background-color: ${({ theme }) => theme.palette.blue[10]};
    box-shadow: 0 1px 2px 0 rgba(93, 108, 164, 0.54);
  `,
  FieldText: styled.p`
    flex-grow: 1;
    overflow: hidden;
    max-width: 95%;
    text-overflow: ellipsis;
    word-break: break-all;
    word-wrap: break-word;
    white-space: ${({ ellipsis }) => (ellipsis ? 'nowrap' : 'normal')};
    color: ${({ theme, hasAnswers, isPreview }) => (hasAnswers ? theme.palette.grey[900] : hexToRgb(theme.palette.primary.light, isPreview ? 1 : 0.54))};
    font-size: ${({ large, theme }) => theme.typography.pxToRem(large ? 13 : 10)};
    font-weight: 500;
    line-height: ${({ large }) => (large ? 1.54 : 1.4)};
  `,
  Button: styled.button`
    display: flex;
    align-items: center;
    margin-left: auto;
    padding-left: 12px;
    cursor: pointer;
    outline: none;
    ::after {
      content: '';
      position: absolute;
      width: 6px;
      height: 6px;
      right: 2px;
      bottom: 2px;
      margin-left: 4px;
      border: ${({ large }) => (large ? 4 : 3)}px solid ${({ theme }) => theme.palette.primary.light};
      border-top-color: transparent;
      border-left-color: transparent;
      ${({ ellipsis, theme }) => !ellipsis && css`
        border-top-color: ${theme.palette.primary.light};
        border-left-color: ${theme.palette.primary.light};
        border-bottom-color: transparent;
        border-right-color: transparent;
      `}
    }
  `,
};

const QuestionRenderer = ({
  answers, large, questions, viewportWidth, isPreview,
}) => {
  const [expandedFields, setExpandedFields] = useState([]);
  const [ellipsisFields, setEllipsisFields] = useState([]);
  const [resized, setResized] = useState(false);

  const questionsRef = useRef(null);

  const handleClick = useCallback((event) => {
    const { dataset } = event.currentTarget;

    setExpandedFields(prevExpandedFields => (includes(toNumber(dataset.number), prevExpandedFields)
      ? reject(expandedField => expandedField === toNumber(dataset.number), prevExpandedFields)
      : [
        ...prevExpandedFields,
        toNumber(dataset.number),
      ]));
  }, []);

  useEffect(() => {
    if (questionsRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        const { contentRect } = entries[0];

        if (contentRect.width) {
          setResized(true);
        }
      });

      if (questionsRef.current.querySelector('#fieldText_1')) {
        resizeObserver.observe(questionsRef.current.querySelector('#fieldText_1'));
      }

      setEllipsisFields(compose(
        map(question => question.number),
        filter(question => isEllipsisActive(questionsRef.current.querySelector(`#fieldText_${question.number}`))),
      )(questions));

      setExpandedFields([]);

      return () => {
        resizeObserver.disconnect();
      };
    }

    return () => {};
  }, [answers, questions]);

  useEffect(() => {
    if (resized) {
      setEllipsisFields(compose(
        map(question => question.number),
        filter(question => isEllipsisActive(questionsRef.current.querySelector(`#fieldText_${question.number}`))),
      )(questions));
    }
  }, [resized, questions]);

  const SelctectionTypeAnswers = (option, question) => {
    const isOtherType = option.type === 'TEXT';
    const isChecked = selectedAnswers => find(answer => (isOtherType
      ? startsWith('other:', answer)
      : answer === toString(option.number)), selectedAnswers);

    const otherAnswer = (selectedAnswers) => {
      const other = find(startsWith('other:'), selectedAnswers);
      return other ? other.replace('other:', '') : '(의견 없음)';
    };

    const hasAnswers = selectedAnswers => !compose(
      isNil,
      find(startsWith('other:')),
    )(selectedAnswers);

    return (
      <>
        {answers
          ? {
            SINGLE_SELECTION: question.type === 'SINGLE_SELECTION' && isChecked([answers[question.number]])
              ? <S.RadioButtonChecked large={large} />
              : <S.RadioButtonUnchecked large={large} />,
            MULTIPLE_SELECTION: question.type === 'MULTIPLE_SELECTION' && isChecked(answers[question.number])
              ? <S.CheckBox large={large} />
              : <S.CheckBoxOutlineBlank large={large} />,
          }[question.type]
          : {
            SINGLE_SELECTION: question.type === 'SINGLE_SELECTION' && <S.RadioButtonUnchecked large={large} />,
            MULTIPLE_SELECTION: question.type === 'MULTIPLE_SELECTION' && <S.CheckBoxOutlineBlank large={large} />,
          }[question.type]}
        {isOtherType
          ? (
            <S.OtherResponseRect
              field={{ position: option.position, size: option.size || { width: 25 } }}
              viewportWidth={viewportWidth}
              large={large}
              hasPseudoElement={!includes(question.number, ellipsisFields)}
              ellipsis={!includes(question.number, expandedFields)}
            >
              <S.FieldText
                id={`fieldText_${question.number}`}
                ellipsis={!includes(question.number, expandedFields)}
                large={large}
                isPreview={isPreview}
                hasAnswers={answers
                  ? {
                    SINGLE_SELECTION: question.type === 'SINGLE_SELECTION' && hasAnswers([answers[question.number]]),
                    MULTIPLE_SELECTION: question.type === 'MULTIPLE_SELECTION' && hasAnswers(answers[question.number]),
                  }[question.type]
                  : false}
              >
                {answers
                  ? {
                    SINGLE_SELECTION: question.type === 'SINGLE_SELECTION' && otherAnswer([answers[question.number]]),
                    MULTIPLE_SELECTION: question.type === 'MULTIPLE_SELECTION' && otherAnswer(answers[question.number]),
                  }[question.type]
                  : '기타 의견을 입력해주세요.'}
              </S.FieldText>
              {answers
              && includes(question.number, ellipsisFields)
              && (includes(question.number, expandedFields)
                ? (
                  <S.Button
                    type="button"
                    data-number={question.number}
                    onClick={handleClick}
                    large={large}
                    ellipsis={!includes(question.number, expandedFields)}
                  />
                )
                : (
                  <S.Button
                    type="button"
                    data-number={question.number}
                    onClick={handleClick}
                    large={large}
                    ellipsis={!includes(question.number, expandedFields)}
                  />
                ))}
            </S.OtherResponseRect>
          )
          : null}
      </>
    );
  };

  const getDescriptiveAnswer = question => (get(question.number, answers) ? answers[question.number] : '(답변 없음)');

  return questions && (
    <ol ref={questionsRef}>
      {map(question => (
        <li key={question.number}>
          <S.QuestionBlock
            number={question.number}
            position={question.position || { x: question.offsetX, y: question.offsetY }}
            required={question.required}
            size={question.size || { width: 0, height: 0 }}
            large={large}
          >
            <S.QuestionNumber large={large}>
              {`Q${question.number}${question.required ? '*' : ''}`}
            </S.QuestionNumber>
          </S.QuestionBlock>
          {question.options && (
            <ul>
              {map(option => (
                // XXX: 하위 호환성 유지를 위한 필드. 추후 제거 예정
                <S.OptionItem
                  key={option.number}
                  position={option.position || { x: option.offsetX, y: option.offsetY }}
                  rounded={question.type === 'SINGLE_SELECTION'}
                  large={large}
                >
                  <S.OptionNumber large={large}>
                    {`${question.number}-${option.number}`}
                  </S.OptionNumber>
                  {SelctectionTypeAnswers(option, question, viewportWidth)}
                </S.OptionItem>
              ), question.options)}
            </ul>
          )}
          {question.field && (
            <S.FieldRect
              field={get('x', question.field)
                ? question.field
                : { x: question.field.offsetX, y: question.field.offsetY, width: 20 }}
              large={large}
              hasPseudoElement={!includes(question.number, ellipsisFields)}
              ellipsis={!includes(question.number, expandedFields)}
            >
              <S.FieldText
                id={`fieldText_${question.number}`}
                ellipsis={!includes(question.number, expandedFields)}
                large={large}
                isPreview={isPreview}
                hasAnswers={get(question.number, answers)}
              >
                {answers ? getDescriptiveAnswer(question) : `${question.number}번 질문 답변`}
              </S.FieldText>
              {answers
                && includes(question.number, ellipsisFields)
                && (includes(question.number, expandedFields)
                  ? (
                    <S.Button
                      type="button"
                      data-number={question.number}
                      onClick={handleClick}
                      large={large}
                      ellipsis={!includes(question.number, expandedFields)}
                    />
                  )
                  : (
                    <S.Button
                      type="button"
                      data-number={question.number}
                      onClick={handleClick}
                      large={large}
                      ellipsis={!includes(question.number, expandedFields)}
                    />
                  ))}
            </S.FieldRect>
          )}
        </li>
      ), questions)}
    </ol>
  );
};

QuestionRenderer.propTypes = {
  answers: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ])),
  large: PropTypes.bool,
  questions: PropTypes.arrayOf(PropTypes.shape({
    number: PropTypes.number.isRequired,
  })),
  viewportWidth: PropTypes.number,
  isPreview: PropTypes.bool,
};

QuestionRenderer.defaultProps = {
  answers: {},
  large: false,
  questions: null,
  viewportWidth: 0,
  isPreview: false,
};

export default memo(QuestionRenderer);
